import React from 'react';
import { Quotes, User, Tag, BookOpen, Image as ImageIcon, Translate } from '@phosphor-icons/react';
import MediaPickerInput from '../../MediaPickerInput';

export default function QuoteModule({ metadata, onChange }) {
  const handleChange = (field, value) => {
    onChange({ ...metadata, [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Quote Text */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Quotes size={16} />
          Quote Text <span className="text-red-500">*</span>
        </label>
        <textarea
          value={metadata.quote_text || ''}
          onChange={(e) => handleChange('quote_text', e.target.value)}
          placeholder="Enter the quote..."
          className="w-full px-6 py-4 bg-background border-2 border-border rounded-lg outline-none focus:border-primary transition-colors resize-none text-xl font-serif italic"
          rows={6}
          style={{ fontFamily: 'Cormorant Garamond, serif', lineHeight: '1.8' }}
        />
      </div>

      {/* Live Preview */}
      {metadata.quote_text && (
        <div className="p-8 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20">
          <div className="relative">
            <Quotes size={48} className="absolute -top-6 -left-2 text-primary/20" weight="fill" />
            <p className="text-2xl font-serif italic text-center relative z-10" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              "{metadata.quote_text}"
            </p>
            {metadata.author_name && (
              <p className="text-right mt-4 text-muted-foreground">
                — {metadata.author_name}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Author Name */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <User size={16} />
          Author / Source
        </label>
        <input
          type="text"
          value={metadata.author_name || ''}
          onChange={(e) => handleChange('author_name', e.target.value)}
          placeholder="Who said or wrote this quote?"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
      </div>

      {/* Theme */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Tag size={16} />
          Theme
        </label>
        <input
          type="text"
          value={metadata.theme || ''}
          onChange={(e) => handleChange('theme', e.target.value)}
          placeholder="e.g., Inspiration, Wisdom, Love, Life"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
      </div>

      {/* Source */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <BookOpen size={16} />
          Source / Reference
        </label>
        <input
          type="text"
          value={metadata.source || ''}
          onChange={(e) => handleChange('source', e.target.value)}
          placeholder="Book, speech, or origin of the quote"
          className="w-full px-4 py-2 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors"
        />
      </div>

      {/* Context / Background */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Context / Background
        </label>
        <textarea
          value={metadata.context || ''}
          onChange={(e) => handleChange('context', e.target.value)}
          placeholder="Provide context about when/where/why this was said..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={3}
        />
      </div>

      {/* Translation */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Translate size={16} />
          Translation (if applicable)
        </label>
        <textarea
          value={metadata.translation || ''}
          onChange={(e) => handleChange('translation', e.target.value)}
          placeholder="English translation if quote is in another language..."
          className="w-full px-4 py-3 bg-background border border-border rounded-lg outline-none focus:border-primary transition-colors resize-none"
          rows={3}
        />
      </div>

      {/* Background Image */}
      <div>
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <ImageIcon size={16} />
          Background Image for Social Sharing
        </label>
        <MediaPickerInput
          value={metadata.background_image || ''}
          onChange={(url) => handleChange('background_image', url)}
          accept="image/*"
          label="Select background image"
          helpText="Will be used when quote is shared on social media"
        />
      </div>
    </div>
  );
}
